import { getConfigString } from '@shared/util/env';
import DiiaEnIcon from '@ui/Icon/library/DiiaEnIcon';
import DiiaIcon from '@ui/Icon/library/DiiaIcon';
import FacebookIcon from '@ui/Icon/library/FacebookIcon';
import GoogleIcon from '@ui/Icon/library/GoogleIcon';
import { ComponentType, type ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import DiiaProvider from './diia';
import FacebookProvider from './facebook';
import GoogleProvider from './google';

export type AuthProvider = 'diia' | 'facebook' | 'google_oauth2';

type ProviderInfo = {
  available: boolean;
  label: string;
  dialog?: boolean;
  Icon: ComponentType | null;
  useProvider: typeof FacebookProvider | typeof GoogleProvider | typeof DiiaProvider | null;
  labelIntl?: ReactElement<{ id: string }>;
  signInIntl?: ReactElement<{ id: string }>;
  signUpIntl?: ReactElement<{ id: string }>;
};

// we keep track of these since users may have accounts with these providers
const inactiveProviders = {
  learnworlds: {
    available: false,
    label: 'chapter.works',
    Icon: null,
    useProvider: null,
  } as ProviderInfo,
};

const activeProviders = (locale?: string): Record<AuthProvider, ProviderInfo> => ({
  diia: {
    available: true,
    Icon: locale === 'uk' ? DiiaIcon : DiiaEnIcon,
    label: 'Diia',
    labelIntl: <FormattedMessage id="login.diia.name" />,
    signInIntl: <FormattedMessage id="login.diia.sign_in_with_diia" />,
    signUpIntl: <FormattedMessage id="login.diia.sign_up_with_diia" />,
    useProvider: DiiaProvider,
    dialog: true,
  },
  facebook: {
    available: !!getConfigString('fb-app-id'),
    Icon: FacebookIcon,
    label: 'Facebook',
    useProvider: FacebookProvider,
  },
  google_oauth2: {
    available: !!getConfigString('g-client-id'),
    Icon: GoogleIcon,
    label: 'Google',
    useProvider: GoogleProvider,
  },
});

const providers = (locale?: string) => ({
  ...activeProviders(locale),
  ...inactiveProviders,
});

export type AnyAuthProvider = keyof ReturnType<typeof providers>;

export default providers;
